import type { DateTimeString, Relation, Relations } from '../types';

import {
  differenceInDays,
  format,
  formatDistanceToNowStrict,
  isToday,
  isYesterday,
  parseISO,
} from 'date-fns';

export { default as formatElapsed } from './format-elapsed';
export { default as formatTimeSpent } from './format-time-spent';
export { default as namePastedFile } from './name-pasted-file';
export { default as parseTimeSpent } from './parse-time-spent';
export { default as remarkSpacedParagraphs } from './remark-spaced-paragraphs';
export { default as remarkTicketLinks } from './remark-ticket-links';
export { default as zeroPad } from './zero-pad';
export * from './day-input';

export function capitalize(name: string): string {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function relationsAsIri<D extends Record<string, unknown>>(
  relations: Relations<D>
): string[] {
  if (!relations) throw new Error('Relation not fetched');
  return relations.map((relation) =>
    typeof relation === 'string'
      ? relation
      : ((relation as Record<string, unknown>)['@id'] as string)
  );
}

export function ifObject<T extends Record<string, unknown>>(
  relation: Relation<T>
): null | T {
  return typeof relation === 'object' ? relation : null;
}

type DateInput = Date | DateTimeString;

function parseDateInput(d: DateInput): Date {
  return typeof d === 'string' ? parseISO(d) : d;
}

/**
 * Like Object.entries but type hinted usefully
 */
export function entries<K extends string, V>(
  obj: Readonly<Partial<Record<K, V>>>
): Array<[K, V]> {
  return (Object.keys(obj) as K[]).map((k) => [k, obj[k] as V]);
}

export function formatAsReadableDate(d: DateInput): string {
  const parsed = parseDateInput(d);
  if (differenceInDays(parsed, new Date()) < 30)
    return formatDistanceToNowStrict(parsed, { addSuffix: true });
  return formatAsDateTime(parsed);
}

export function formatAsDate(d: DateInput): string {
  const parsed = parseDateInput(d);
  return format(parsed, 'yyyy-MM-dd');
}

/**
 * A day named the way people speak of it, so Today and Yesterday stand out
 * from the dates around them.
 */
export function formatAsDayLabel(d: DateInput): string {
  const parsed = parseDateInput(d);
  const label = format(parsed, 'EEE d MMM');

  if (isToday(parsed)) return `Today · ${label}`;
  if (isYesterday(parsed)) return `Yesterday · ${label}`;
  return label;
}

export function formatAsDateTime(d: DateInput): string {
  const parsed = parseDateInput(d);
  return format(parsed, "EEE d MMM y 'at' HH:mm");
}

export function formatAsISO(d: DateInput): string {
  const parsed = parseDateInput(d);
  return format(parsed, "yyyy-MM-dd'T'HH:mm:ssxxx");
}

/**
 * A number rounded to at most one decimal place, and dropping it where it
 * rounds clean, for the "6.2 of 12 hours" reads beside a bar.
 */
export function formatTenths(value: number): string {
  return String(Math.round(value * 10) / 10);
}

type QueryParamValue = string | number | boolean | string[] | null | undefined;

export function makeQueryString(
  params: Record<string, QueryParamValue> = {}
): string {
  const printValue = (v: Exclude<QueryParamValue, string[]>): string => {
    if (typeof v === 'boolean') return v ? 'true' : 'false';
    return encodeURIComponent(String(v));
  };
  const qs = entries(params)
    .filter(([, v]) => v != null)
    .flatMap(([k, v]) =>
      Array.isArray(v)
        ? v.map((item) => `${k}=${printValue(item)}`)
        : [`${k}=${printValue(v)}`]
    )
    .join('&');
  return qs ? `?${qs}` : '';
}

// Like Object.values but type hinted usefully
export function values<T>(
  obj: Readonly<{
    [key: string]: T;
  }>
): T[] {
  return Object.keys(obj).map((k) => obj[k]);
}
